/*
 * Purpose: Manage compound password + OTP state for reset/update flows.
 * Boundary: Custom hook only; coordinates child component states; no UI rendering.
 * Invariants:
 * - Tracks password and OTP validity independently.
 * - Overall validity requires both password and OTP to be valid.
 * - Exposes setPasswordState and setOtpState for child components.
 * - Channel selection: derives available channels from locale and confirmed email.
 * - Silent email check fires when ID is valid; result adds email channel option.
 * - ASCII only; comments and strings are plain ASCII.
 */

// Imports
import { useState, useCallback, useMemo, useEffect } from 'react';

// Hook usePasswordWithOtp:
// Manages compound state for password and OTP components
export function usePasswordWithOtp({
    auth,
    type = 'reset',
    idNumber = '',
    cellNumber = '',
    isValidID = false,
    isProceedReadyOtp = false,
    heading
} = {}) {
    // States: Password and OTP validity tracking
    const [passwordState, setPasswordState] = useState({
        isValid: false,
        password: ''
    });

    const [otpState, setOtpState] = useState({
        isValid: false,
        otpValue: '',
        hasRequested: false,
        cooldown: 0
    });

    // States: Channel selection
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [availableChannels, setAvailableChannels] = useState([]);
    const [confirmedEmail, setConfirmedEmail] = useState(null);
    const [maskedEmail, setMaskedEmail] = useState(null);

    // Derived: Locale from phone number prefix
    const isLocal = useMemo(() => {
        if (!cellNumber) return true;
        return cellNumber.startsWith('+27') || cellNumber.startsWith('27');
    }, [cellNumber]);

    // Effect: Silent email check when ID becomes valid
    useEffect(() => {
        if (!isValidID || !idNumber || !auth?.checkConfirmedEmail) return;

        let cancelled = false;
        auth.checkConfirmedEmail({ idNumber })
            .then(result => {
                if (cancelled) return;
                if (result?.data?.hasConfirmedEmail && result?.data?.maskedEmailAddress) {
                    setConfirmedEmail(true);
                    setMaskedEmail(result.data.maskedEmailAddress);
                } else if (result?.hasConfirmedEmail && result?.maskedEmailAddress) {
                    // Alternate response shape (direct, no wrapper)
                    setConfirmedEmail(true);
                    setMaskedEmail(result.maskedEmailAddress);
                }
            })
            .catch(() => {
                // Silent failure -- no email option available
            });

        return () => { cancelled = true; };
    }, [isValidID, idNumber, auth]);

    // Effect: Derive available channels from locale and confirmed email
    useEffect(() => {
        const channels = isLocal ? ['sms', 'whatsapp'] : ['whatsapp'];
        if (confirmedEmail) channels.push('email');
        setAvailableChannels(channels);
        setSelectedChannel(prev => prev && channels.includes(prev) ? prev : (channels.includes('whatsapp') ? 'whatsapp' : channels[0]));
    }, [isLocal, confirmedEmail]);

    // Handlers: Child component state setters
    const handleSetPasswordState = useCallback((state) => {
        setPasswordState(state);
    }, []);

    const handleSetOtpState = useCallback((state) => {
        setOtpState(state);
    }, []);

    // Handler: Channel selection
    const handleSetSelectedChannel = useCallback((channel) => {
        setSelectedChannel(channel);
    }, []);

    // Derived: Overall validity requires both password and OTP
    const isValid = useMemo(
        () => passwordState.isValid && otpState.isValid,
        [passwordState.isValid, otpState.isValid]
    );

    // Derived: Whether to show channel selector (2+ channels available)
    const showChannelSelector = availableChannels.length >= 2;

    // Derived: Channel-aware warning text
    const warningText = useMemo(() => {
        if (!selectedChannel) return '';

        const channelLabel = selectedChannel === 'sms' ? 'SMS'
            : selectedChannel === 'whatsapp' ? 'WhatsApp'
            : 'Email';

        // Destination differs for email vs phone channels
        const destination = selectedChannel === 'email' ? maskedEmail : cellNumber;

        if (otpState.hasRequested) {
            const checkHint = selectedChannel === 'email'
                ? 'check your inbox'
                : 'check your messages';
            if (selectedChannel === 'email') {
                return `Verification code sent to ${destination} -- ${checkHint}`;
            }
            return `Verification code sent to ${destination} via ${channelLabel} -- ${checkHint}`;
        }

        if (selectedChannel === 'email') {
            return `A verification code will be sent to ${destination}`;
        }
        return `A verification code will be sent to ${destination} via ${channelLabel}`;
    }, [selectedChannel, cellNumber, maskedEmail, otpState.hasRequested]);

    // Return: Grouped state and handlers for layout
    return {
        // Auth and context
        auth,
        type,
        heading,
        idNumber,
        cellNumber,
        isProceedReadyOtp,

        // Password state
        isPasswordValid: passwordState.isValid,
        password: passwordState.password,
        setPasswordState: handleSetPasswordState,

        // OTP state
        isOtpValid: otpState.isValid,
        otpValue: otpState.otpValue,
        otpCooldown: otpState.cooldown,
        otpHasRequested: otpState.hasRequested,
        setOtpState: handleSetOtpState,

        // Channel selection
        selectedChannel,
        setSelectedChannel: handleSetSelectedChannel,
        availableChannels,
        showChannelSelector,
        warningText,
        maskedEmail,

        // Compound state
        isValid
    };
}
